import React, { useState, useEffect } from 'react';
import { getUserDetails } from '@apiClient/user';
import { IconButton, Typography, Tabs, Tab, Box, Modal } from '@mui/material';
import {
  Close as CloseIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  MoreVert,
} from '@mui/icons-material';
import MyAvatar from './MyAvatar';
import PageNavigator from './PageNavigator';
import { ModalBox } from '@components/admin/StyledComponents';
import DetailRow from './DetailRow';
import greenCommunityIcon from '../../assets/img/green community.png';
import greenConnectionIcon from '../../assets/img/green connection.png';
import greenGroupIcon from '../../assets/img/green group.png';
import profileIcon from '../../assets/img/profile.png';
import communityIcon from '../../assets/img/community.png';
import groupIcon from '../../assets/img/group.png';
import connectionIcon from '../../assets/img/connection.png';
import userMemberIcon from '../../assets/img/userMember.png';
import scheduleIcon from '../../assets/img/schedule.png';
import emailIcon from '../../assets/img/email.png';
import redCircle from '../../assets/img/red-circle-emoji.png';
import greenCircle from '../../assets/img/green-circle-emoji.png';
import SearchBar from './SearchBar';
import PopperWrapper from '@components/admin/PopperWrapper';
import PopperList from '@components/admin/PopperList';
import ExpandableText from "@components/ExpandableText";

const getNestedValue = (obj, path) => {
  if (!path) return obj;
  return path.split('.').reduce((acc, part) => acc && acc[part], obj);
};

function TabPanel({ children, value, index, ...other }) {
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ width: "100%" }}>{children}</Box>}
    </div>
  );
}

function ProfileTab({ user }) {
  return (
    <div>
      <table className="w-full text-left text-sm mt-2">
        <tbody>
          <DetailRow
            icon={userMemberIcon}
            label="Username:"
            value={user.userName || 'N/A'}
          />
          <DetailRow
            icon={emailIcon}
            label="Email:"
            value={user.emailAddress || 'N/A'}
          />
          <DetailRow
            label="Created On:"
            icon={scheduleIcon}
            value={
              user.createdAt
                ? new Date(user.createdAt).toLocaleDateString('en-GB', {
                    day: '2-digit',
                    month: 'long',
                    year: 'numeric',
                  })
                : 'N/A'
            }
          />
          <DetailRow
            label="Status:"
            value={user.disabledAt > 0 ? 'Disabled' : 'Active'}
            icon={user.disabledAt > 0 ? redCircle : greenCircle}
          />
        </tbody>
      </table>
    </div>
  );
}

function CommunitiesTab({ user, fetchPage }) {
  const [searchText, setSearchText] = useState('');
  const [sortConfig, setSortConfig] = useState({
    key: 'name',
    direction: 'asc',
  });

  const meta = user.communities || {};
  const items = Array.isArray(user.communities)
    ? user.communities
    : meta.items || user._raw?.communities || [];

  const page = meta.page || 1;
  const perPage = meta.perPage || 10;

  const filtered = items.filter((c) =>
    c.name.toLowerCase().includes(searchText.toLowerCase())
  );

  const sorted = React.useMemo(() => {
    if (!sortConfig.key) return filtered;
    return [...filtered].sort((a, b) => {
      const aVal = a[sortConfig.key] || '';
      const bVal = b[sortConfig.key] || '';
      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filtered, sortConfig]);

  const goPage = (newPage) => {
    fetchPage(newPage, perPage);
  };

  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  return (
    <div>
      {/* 🔵 Sticky Title + SearchBar */}
      <div className="flex justify-between items-center mb-0 sticky top-0 bg-white py-3 px-3 z-30">
        <Typography variant="h6" className="font-bold">
          All Communities{' '}
          <span className="font-semibold">({meta.total || 0})</span>
        </Typography>
        <div className="w-64">
          <SearchBar setQuery={setSearchText} query={searchText} />
        </div>
      </div>

      {/* 🔵 Sticky Table Header */}
      <table className="w-full table-fixed text-sm">
        <thead className="sticky bg-white z-20" style={{ top: 56 }}>
          <tr className="border-b text-grey-500">
            <th
              className="text-left py-3 font-semibold px-6 whitespace-nowrap cursor-pointer w-1/4"
              onClick={() => requestSort('name')}
            >
              <div className="flex items-center">
                <span>Name</span>
                <span className="ml-2">
                  {sortConfig.key === 'name' ? (
                    sortConfig.direction === 'asc' ? (
                      <KeyboardArrowDownIcon fontSize="small" />
                    ) : (
                      <KeyboardArrowUpIcon fontSize="small" />
                    )
                  ) : (
                    <KeyboardArrowUpIcon
                      fontSize="small"
                      style={{ opacity: 0.3 }}
                    />
                  )}
                </span>
              </div>
            </th>
            <th
              className="text-left py-3 font-semibold px-12 cursor-pointer"
              onClick={() => requestSort('userRole')}
            >
              <div className="flex items-center">
                <span>Role</span>
                <span className="ml-2">
                  {sortConfig.key === 'userRole' ? (
                    sortConfig.direction === 'asc' ? (
                      <KeyboardArrowDownIcon fontSize="small" />
                    ) : (
                      <KeyboardArrowUpIcon fontSize="small" />
                    )
                  ) : (
                    <KeyboardArrowUpIcon
                      fontSize="small"
                      style={{ opacity: 0.3 }}
                    />
                  )}
                </span>
              </div>
            </th>
            <th
              className="text-left py-3 font-semibold px-6 cursor-pointer"
              onClick={() => requestSort('type')}
            >
              <div className="flex items-center">
                <span>Type</span>
                <span className="ml-2">
                  {sortConfig.key === 'type' ? (
                    sortConfig.direction === 'asc' ? (
                      <KeyboardArrowDownIcon fontSize="small" />
                    ) : (
                      <KeyboardArrowUpIcon fontSize="small" />
                    )
                  ) : (
                    <KeyboardArrowUpIcon
                      fontSize="small"
                      style={{ opacity: 0.3 }}
                    />
                  )}
                </span>
              </div>
            </th>
            <th className="text-left py-3 font-semibold px-3">Groups</th>
            <th className="text-left py-3 font-semibold px-3">Members</th>
          </tr>
        </thead>
        <tbody>
          {sorted.length === 0 ? (
            <tr>
              <td colSpan={5} className="text-center py-6 text-gray-500">
                No record found
              </td>
            </tr>
          ) : (
            sorted.map((community) => (
              <tr key={community.id} className="border-b">
                <td className="py-4 px-6">
                  <div className="flex items-center space-x-1">
                    <div
                      className="w-8 h-8 rounded-full bg-gray-100 text-gray-600 flex items-center justify-center overflow-hidden border border-gray-200"
                      style={{ minWidth: '2rem', minHeight: '2rem' }}
                    >
                      {' '}
                      {community?.icon?.currBucketUrl ? (
                        <img
                          src={community.icon.currBucketUrl}
                          alt={community?.name}
                          className="w-full h-full object-cover rounded-full"
                        />
                      ) : (
                        <span className="text-sm font-medium">
                          {community?.name?.[0]?.toUpperCase() || 'C'}
                        </span>
                      )}
                    </div>
                    <span className="text-sm font-medium">
                      {community.name}
                    </span>
                  </div>
                </td>

                <td className="py-3 px-12">{community.userRole}</td>
                <td className="py-3 px-6">{community.type}</td>
                <td className="py-3 px-3">{community.groupsCount || 0}</td>
                <td className="py-3 px-3">{community.membersCount || 0}</td>
              </tr>
            ))
          )}
        </tbody>
      </table>

      {/* 🔵 Sticky Pagination */}
      <div className="sticky bottom-0 bg-white py-5 px-3 z-30 mb-3">
        <PageNavigator
          totalPages={
            meta.totalPages ||
            Math.max(1, Math.ceil((meta.total || items.length) / perPage))
          }
          setPage={(p) => goPage(p)}
          page={page}
          setLimit={(l) => fetchPage(1, l)}
          limit={perPage}
        />
      </div>
    </div>
  );
}

function GroupsTab({ user, fetchPage }) {
  const [searchText, setSearchText] = useState('');
  const [sortConfig, setSortConfig] = useState({
    key: 'name',
    direction: 'asc',
  });
  const meta = user.groups || {};
  const groupsData = Array.isArray(user.groups)
    ? user.groups
    : meta.items || user._raw?.groups || [];
  const page = meta.page || 1;
  const perPage = meta.perPage || 10;

  const filteredGroups = groupsData.filter((group) =>
    group.name.toLowerCase().includes(searchText.toLowerCase())
  );

  const sortedGroups = React.useMemo(() => {
    if (!sortConfig.key) return filteredGroups;
    return [...filteredGroups].sort((a, b) => {
      const aVal = (getNestedValue(a, sortConfig.key) || '').toLowerCase();
      const bVal = (getNestedValue(b, sortConfig.key) || '').toLowerCase();
      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredGroups, sortConfig]);

  const goPage = (newPage) => {
    fetchPage(newPage, perPage);
  };

  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-0 sticky top-0 bg-white py-3 px-3 z-30 border-b">
        <Typography variant="h6" className="font-bold">
          All Groups{' '}
          <span className="font-semibold">
            ({meta.total || meta.length || 0})
          </span>
        </Typography>
        <div className="w-64">
          <SearchBar setQuery={setSearchText} query={searchText} />
        </div>
      </div>

      <table className="w-full table-fixed text-sm">
        <thead className="sticky bg-white z-20" style={{ top: "56px" }}>
          <tr className="border-b text-gray-500">
            <th
              className="text-left py-3 font-semibold px-6 whitespace-nowrap cursor-pointer w-1/4"
              onClick={() => requestSort('name')}
            >
              <div className="flex items-center">
                <span>Name</span>
                <span className="ml-2">
                  {sortConfig.key === 'name' ? (
                    sortConfig.direction === 'asc' ? (
                      <KeyboardArrowDownIcon fontSize="small" />
                    ) : (
                      <KeyboardArrowUpIcon fontSize="small" />
                    )
                  ) : (
                    <KeyboardArrowUpIcon
                      fontSize="small"
                      style={{ opacity: 0.3 }}
                    />
                  )}
                </span>
              </div>
            </th>
            <th
              className="text-left py-3 font-semibold px-12 cursor-pointer"
              onClick={() => requestSort('userRole')}
            >
              <div className="flex items-center">
                <span>Role</span>
                <span className="ml-2">
                  {sortConfig.key === 'userRole' ? (
                    sortConfig.direction === 'asc' ? (
                      <KeyboardArrowDownIcon fontSize="small" />
                    ) : (
                      <KeyboardArrowUpIcon fontSize="small" />
                    )
                  ) : (
                    <KeyboardArrowUpIcon
                      fontSize="small"
                      style={{ opacity: 0.3 }}
                    />
                  )}
                </span>
              </div>
            </th>
            <th
              className="text-left py-3 font-semibold px-6 whitespace-nowrap cursor-pointer"
              onClick={() => requestSort('visibility')}
            >
              <div className="flex items-center">
                <span>Type</span>
                <span className="ml-2">
                  {sortConfig.key === 'visibility' ? (
                    sortConfig.direction === 'asc' ? (
                      <KeyboardArrowDownIcon fontSize="small" />
                    ) : (
                      <KeyboardArrowUpIcon fontSize="small" />
                    )
                  ) : (
                    <KeyboardArrowUpIcon
                      fontSize="small"
                      style={{ opacity: 0.3 }}
                    />
                  )}
                </span>
              </div>
            </th>
            <th
              className="text-left py-3 font-semibold px-3 whitespace-nowrap cursor-pointer"
              onClick={() => requestSort('community.name')}
            >
              <div className="flex items-center">
                <span>Community</span>
                <span className="ml-2">
                  {sortConfig.key === 'community.name' ? (
                    sortConfig.direction === 'asc' ? (
                      <KeyboardArrowDownIcon fontSize="small" />
                    ) : (
                      <KeyboardArrowUpIcon fontSize="small" />
                    )
                  ) : (
                    <KeyboardArrowUpIcon
                      fontSize="small"
                      style={{ opacity: 0.3 }}
                    />
                  )}
                </span>
              </div>
            </th>
            <th className="text-left py-3 font-semibold px-6 whitespace-nowrap">
              Members
            </th>
          </tr>
        </thead>

        <tbody>
          {sortedGroups.length === 0 ? (
            <tr>
              <td colSpan={5} className="text-center py-6 text-gray-500">
                No record found
              </td>
            </tr>
          ) : (
            sortedGroups.map((group) => (
              <tr key={group.id} className="border-b">
                {/* Group Name + Icon (merged column) */}
                <td className="py-4 px-6">
                  <div className="flex items-center space-x-1">
                    <div
                      className="w-8 h-8 rounded-full bg-gray-100 text-gray-600 flex items-center justify-center overflow-hidden border border-gray-200"
                      style={{ minWidth: '2rem', minHeight: '2rem' }}
                    >
                      {group.icon?.currBucketUrl ? (
                        <img
                          src={group.icon.currBucketUrl}
                          alt={group.name}
                          className="w-full h-full object-cover rounded-full"
                        />
                      ) : (
                        <span className="text-sm font-medium">
                          {group.name?.[0]?.toUpperCase()}
                        </span>
                      )}
                    </div>
                    <span className="font-medium">{group.name}</span>
                  </div>
                </td>

                {/* Role */}
                <td className="py-4 px-6">{group.userRole || 'Member'}</td>

                {/* Type */}
                <td className="py-4 px-6">{group.visibility}</td>

                {/* Community */}
                <td className="py-4 px-3">
                  <div className="flex items-center space-x-1">
                    <div
                      className="w-8 h-8 rounded-full bg-gray-100 text-gray-600 flex items-center justify-center overflow-hidden border border-gray-200"
                      style={{ minWidth: '2rem', minHeight: '2rem' }}
                    >
                      {group.community?.icon?.currBucketUrl ? (
                        <img
                          src={group.community.icon.currBucketUrl}
                          alt={group.community?.name}
                          className="w-full h-full object-cover rounded-full"
                        />
                      ) : (
                        <span className="text-sm font-medium">
                          {group.community?.name?.[0]?.toUpperCase() || 'C'}
                        </span>
                      )}
                    </div>
                    <span>{group.community?.name || 'Community'}</span>
                  </div>
                </td>

                {/* Members */}
                <td className="py-4 px-6">{group.membersCount || 0}</td>
              </tr>
            ))
          )}
        </tbody>
      </table>

      <div className="sticky bottom-0 bg-white py-3 px-3 z-20 mb-3">
        <PageNavigator
          totalPages={
            meta.totalPages ||
            Math.max(1, Math.ceil((meta.total || groupsData.length) / perPage))
          }
          setPage={(p) => goPage(p)}
          page={page}
          setLimit={(l) => fetchPage(1, l)}
          limit={perPage}
        />
      </div>
    </div>
  );
}

function ConnectionsTab({ user, fetchPage, onViewProfile }) {
  const [searchText, setSearchText] = useState('');
  const [sortConfig, setSortConfig] = useState({
    key: 'firstName',
    direction: 'asc',
  });
  const meta = user.connectionsList || {};
  const items = Array.isArray(user.connectionsList)
    ? user.connectionsList
    : meta.items || user._raw?.connectionsList || [];
  const page = meta.page || 1;
  const perPage = meta.perPage || 10;

  const filtered = items.filter(
    (c) =>
      `${c.firstName} ${c.lastName}`
        .toLowerCase()
        .includes(searchText.toLowerCase()) ||
      c.userName?.toLowerCase().includes(searchText.toLowerCase())
  );

  const sorted = React.useMemo(() => {
    if (!sortConfig.key) return filtered;
    return [...filtered].sort((a, b) => {
      let aVal, bVal;
      if (sortConfig.key === 'firstName') {
        aVal = `${a.firstName || ''} ${a.lastName || ''}`.toLowerCase().trim();
        bVal = `${b.firstName || ''} ${b.lastName || ''}`.toLowerCase().trim();
      } else {
        aVal = (a[sortConfig.key] || '').toLowerCase();
        bVal = (b[sortConfig.key] || '').toLowerCase();
      }

      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filtered, sortConfig]);

  const goPage = (newPage) => {
    fetchPage(newPage, perPage);
  };

  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-0 sticky top-0 bg-white py-3 px-3 z-30">
        <Typography variant="h6" className="font-bold">
          All connections{' '}
          <span className="font-semibold">
            ({meta.total || meta.length || 0})
          </span>
        </Typography>
        <div className="w-64">
          <SearchBar setQuery={setSearchText} query={searchText} />
        </div>
      </div>

      <table className="w-full table-fixed text-sm">
        <thead className="sticky bg-white z-20" style={{ top: 56 }}>
          <tr className="border-b text-gray-500">
            <th
              className="text-left py-3 font-semibold px-6 break-words whitespace-normal cursor-pointer"
              onClick={() => requestSort('firstName')}
            >
              <div className="flex items-center">
                <span>Name</span>
                <span className="ml-2">
                  {sortConfig.key === 'firstName' ? (
                    sortConfig.direction === 'asc' ? (
                      <KeyboardArrowDownIcon fontSize="small" />
                    ) : (
                      <KeyboardArrowUpIcon fontSize="small" />
                    )
                  ) : (
                    <KeyboardArrowUpIcon
                      fontSize="small"
                      style={{ opacity: 0.3 }}
                    />
                  )}
                </span>
              </div>
            </th>
            <th
              className="text-left py-3 font-semibold px-6 break-words whitespace-normal cursor-pointer"
              onClick={() => requestSort('userName')}
            >
              <div className="flex items-center">
                <span>Username</span>
                <span className="ml-2">
                  {sortConfig.key === 'userName' ? (
                    sortConfig.direction === 'asc' ? (
                      <KeyboardArrowDownIcon fontSize="small" />
                    ) : (
                      <KeyboardArrowUpIcon fontSize="small" />
                    )
                  ) : (
                    <KeyboardArrowUpIcon
                      fontSize="small"
                      style={{ opacity: 0.3 }}
                    />
                  )}
                </span>
              </div>
            </th>
            <th className="text-left py-3 font-semibold px-6 break-words whitespace-normal">
              Email
            </th>
            <th className="text-left py-3 font-semibold px-6 break-words whitespace-normal">
              Actions
            </th>
          </tr>
        </thead>
        <tbody>
          {sorted.length === 0 ? (
            <tr>
              <td colSpan={5} className="text-center py-6 text-gray-500">
                No record found
              </td>
            </tr>
          ) : (
            sorted.map((connection) => (
              <tr key={connection.id} className="border-b">
                <td className="py-4 px-6 flex items-center space-x-1">
                  <MyAvatar
                    src={connection.media?.currBucketUrl}
                    size="small"
                    className="w-8 h-8 rounded-full"
                  />

                  <div className="text-sm font-medium">
                    {`${connection.firstName || ''} ${
                      connection.lastName || ''
                    }`.trim() || connection.userName}
                  </div>
                </td>

                <td className="py-3 px-6 break-words whitespace-normal">
                  <div className="font-medium break-words whitespace-normal">
                    {connection.userName}
                  </div>
                </td>

                <td className="py-3 px-6 break-words whitespace-normal">
                  <div className="text-sm text-gray-600 break-words whitespace-normal">
                    {connection.email}
                  </div>
                </td>

                <td className="py-3 px-6">
                  <button
                    className="px-4 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 text-sm py-1"
                    onClick={() =>
                      onViewProfile && onViewProfile(connection.id)
                    }
                  >
                    View Profile
                  </button>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>

      <div className="sticky bottom-0 bg-white py-5 px-3 z-20 mb-3">
        <PageNavigator
          totalPages={
            meta.totalPages ||
            Math.max(1, Math.ceil((meta.total || items.length) / perPage))
          }
          setPage={(p) => goPage(p)}
          page={page}
          setLimit={(l) => fetchPage(1, l)}
          limit={perPage}
        />
      </div>
    </div>
  );
}

export default function UserDetails({
  open,
  onClose,
  user,
  actions = [],
  onlyProfile = false,
}) {
  const [activeTab, setActiveTab] = useState(0);
  const [userDetails, setUserDetails] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    async function fetchUserDetails() {
      if (user?.id) {
        setLoading(true);
        try {
          const details = await getUserDetails(user.id, { perPage: 10 });
          setUserDetails(details);
        } catch (error) {
          console.error('Error fetching user details:', error);
        } finally {
          setLoading(false);
        }
      } else {
        setUserDetails(null);
      }
    }

    fetchUserDetails();
    setActiveTab(0);
  }, [user]);

  if (!userDetails) return null;

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const fetchPage = async (tab, page = 1, perPage = 10) => {
    const targetId = userDetails?.id || user?.id;
    if (!targetId) return;
    try {
      setLoading(true);
      const params = { perPage };
      params.tab = tab;
      params.page = page;
      const details = await getUserDetails(targetId, params);
      setUserDetails(details);
    } catch (err) {
      console.error('Error fetching page:', err);
    } finally {
      setLoading(false);
    }
  };

  const openUserProfile = async (connectionId) => {
    if (!connectionId) return;
    try {
      setLoading(true);
      const details = await getUserDetails(connectionId, { perPage: 10 });
      setUserDetails(details);
      setActiveTab(0);
    } catch (err) {
      console.error('Error opening user profile:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
    >
      <ModalBox
        className="shadow-md"
        sx={{
          width: '90%',
          maxWidth: 800,
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          borderRadius: '16px',
          overflow: 'hidden',
        }}
      >
        {/* ===== Header Section ===== */}
        <div className="flex justify-between items-start p-6 border-b bg-white relative">
          <div className="flex items-start">
            <MyAvatar
              src={userDetails.media?.currBucketUrl}
              size="large"
              className="w-24 h-24 rounded-lg"
            />
            <div className="ml-6">
              <Typography variant="h5" className="font-semibold mb-2">
                {userDetails.firstName} {userDetails.lastName}
              </Typography>
              <Typography className="text-gray-600 text-sm leading-relaxed max-w-xl mb-4">
                <ExpandableText
                  defaultShowMore
                  text={userDetails?.about || "No bio available"}
                  truncateClasses="line-clamp-2"
                />
              </Typography>
              <div className="flex items-center space-x-8 flex-nowrap">
                <div className="flex items-center space-x-2 whitespace-nowrap">
                  <img
                    src={greenConnectionIcon}
                    alt="Connections"
                    style={{ width: 18, height: 18 }}
                  />
                  <div className="text-sm whitespace-nowrap">
                    <span className="font-medium">Connections:</span>
                    <span className="font-semibold ml-1">
                      {Array.isArray(userDetails.connectionsList)
                        ? userDetails.connectionsList.length
                        : userDetails.connectionsList?.total ||
                          userDetails._raw?.connectionsList?.length ||
                          0}
                    </span>
                  </div>
                </div>

                <div className="flex items-center space-x-2 whitespace-nowrap">
                  <img
                    src={greenGroupIcon}
                    alt="Groups"
                    style={{ width: 18, height: 18 }}
                  />
                  <div className="text-sm whitespace-nowrap">
                    <span className="font-medium">Groups Joined:</span>
                    <span className="font-semibold ml-1">
                      {Array.isArray(userDetails.groups)
                        ? userDetails.groups.length
                        : userDetails.groups?.total ||
                          userDetails._raw?.groups?.length ||
                          0}
                    </span>
                  </div>
                </div>

                <div className="flex items-center space-x-2 whitespace-nowrap">
                  <img
                    src={greenCommunityIcon}
                    alt="Communities"
                    style={{ width: 18, height: 18 }}
                  />
                  <div className="text-sm whitespace-nowrap">
                    <span className="font-medium">Communities Joined:</span>
                    <span className="font-semibold ml-1">
                      {Array.isArray(userDetails.communities)
                        ? userDetails.communities.length
                        : userDetails.communities?.total ||
                          userDetails._raw?.communities?.length ||
                          0}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="flex items-center">
            {/* Only render PopperWrapper if user is NOT admin */}
            {!userDetails.role.includes("admin") && !onlyProfile && (
              <PopperWrapper id={userDetails?.id || ""}>
                <IconButton>
                  <MoreVert />
                </IconButton>
                <div>
                  <PopperList items={actions} />
                </div>
              </PopperWrapper>
            )}

            <IconButton
              onClick={onClose}
              sx={{
                color: '#10b981',
                '&:hover': {
                  backgroundColor: 'rgba(16, 185, 129, 0.1)',
                },
              }}
            >
              <CloseIcon />
            </IconButton>
          </div>
        </div>

        {/* ===== Tabs Section ===== */}
        {!onlyProfile && (
          <Box sx={{ borderBottom: 1, borderColor: "divider", px: 6, pt: 2 }}>
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              aria-label="user tabs"
              sx={{
                minHeight: "48px",
                "& .MuiTabs-indicator": {
                  display: "none",
                },
                "& .MuiTabs-flexContainer": {
                  gap: "52px",
                },
              }}
            >
              {[
                { icon: profileIcon, label: "Profile" },
                { icon: communityIcon, label: "Communities" },
                { icon: groupIcon, label: "Groups" },
                { icon: connectionIcon, label: "Connections" },
              ].map((tab, index) => (
                <Tab
                  key={tab.label}
                  icon={
                    <img
                      src={tab.icon}
                      alt={tab.label}
                      style={{ width: 18, height: 18, marginRight: 6 }}
                    />
                  }
                  iconPosition="start"
                  label={tab.label}
                  sx={{
                    textTransform: "none",
                    minHeight: "40px",
                    minWidth: "auto",
                    padding: "8px 0",
                    fontSize: "14px",
                    fontWeight: 500,
                    color: "#6b7280",
                    borderRadius: "8px 8px 0 0",
                    backgroundColor: "transparent",
                    borderBottom:
                      activeTab === index
                        ? "3px solid #060606"
                        : "3px solid transparent",
                    transition: "all 0.2s",
                    "&:hover": {
                      color: "#111827",
                      backgroundColor: "#f9fafb",
                    },
                    "&.Mui-selected": {
                      color: "#070707",
                      fontWeight: 600,
                    },
                  }}
                />
              ))}
            </Tabs>
          </Box>
        )}

        {/* ===== Tab Panels ===== */}
        <div
          className="w-full flex-1 overflow-y-auto  px-1"
          style={{ minHeight: 0 }}
        >
          {onlyProfile ? (
            <ProfileTab user={userDetails} />
          ) : (
            <>
              <TabPanel value={activeTab} index={0}>
                <ProfileTab user={userDetails} />
              </TabPanel>
              <TabPanel value={activeTab} index={1}>
                <CommunitiesTab
                  user={userDetails}
                  fetchPage={(page, perPage) =>
                    fetchPage("communities", page, perPage)
                  }
                />
              </TabPanel>
              <TabPanel value={activeTab} index={2}>
                <GroupsTab
                  user={userDetails}
                  fetchPage={(page, perPage) =>
                    fetchPage("groups", page, perPage)
                  }
                />
              </TabPanel>
              <TabPanel value={activeTab} index={3}>
                <ConnectionsTab
                  user={userDetails}
                  fetchPage={(page, perPage) =>
                    fetchPage("connections", page, perPage)
                  }
                  onViewProfile={(id) => openUserProfile(id)}
                />
              </TabPanel>
            </>
          )}
        </div>
      </ModalBox>
    </Modal>
  );
}
